import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, FileText, FileCheck2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const STATUS_COLOR = {
  draft:     { bg: "#F1F5F9", fg: "#475569" },
  sent:      { bg: "#DBEAFE", fg: "#1D4ED8" },
  accepted:  { bg: "#DCFCE7", fg: "#15803D" },
  rejected:  { bg: "#FEE2E2", fg: "#B91C1C" },
  expired:   { bg: "#FEF3C7", fg: "#B45309" },
  converted: { bg: "#E0E7FF", fg: "#4338CA" },
};
const STATUSES = ["draft", "sent", "accepted", "rejected", "expired", "converted"];

async function downloadPdf(id, number) {
  const token = sessionStorage.getItem("miro_token");
  const res = await fetch(`${API}/quotations/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` }});
  if (!res.ok) return toast.error("Download failed");
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${number}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  toast.success(`${number}.pdf downloaded`);
}

export default function Quotations() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => setItems((await api.get("/quotations")).data);
  useEffect(() => { load(); }, []);

  const convert = async (id) => {
    try {
      const r = await api.post(`/invoices/from-quotation/${id}`);
      toast.success(`Invoice ${r.data.number} created`);
      load();
    } catch (e) { toast.error("Convert failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete quotation?")) return;
    await api.delete(`/quotations/${id}`);
    load();
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/quotations/${id}`, { status });
    load();
  };

  return (
    <AppLayout title="Quotations">
      <div className="flex justify-end mb-4">
        <NewQuotationDialog open={open} setOpen={setOpen} onCreated={load}/>
      </div>

      <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-x-auto">
        <table data-testid="quotations-table" className="w-full text-sm">
          <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Number</th>
              <th className="px-4 py-3 font-semibold">Client</th>
              <th className="px-4 py-3 font-semibold">Title</th>
              <th className="px-4 py-3 font-semibold text-right">Total</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Issued</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">No quotations yet.</td></tr>
            )}
            {items.map(q => (
              <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-t border-[var(--miro-border)]">
                <td className="px-4 py-3 font-mono font-semibold text-[var(--miro-navy)]">{q.number}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[var(--miro-navy)]">{q.client_name}</div>
                  <div className="text-xs text-[var(--miro-text-muted)]">{q.client_email || "—"}</div>
                </td>
                <td className="px-4 py-3 text-[var(--miro-text-secondary)]">{q.title}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-[var(--miro-navy)]">₹{q.total.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">
                  <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                    <SelectTrigger data-testid={`quote-status-${q.id}`} className="h-8 w-28 text-xs"
                                   style={{ background: STATUS_COLOR[q.status]?.bg, color: STATUS_COLOR[q.status]?.fg, border: "none" }}>
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-xs text-[var(--miro-text-muted)]">{formatDistanceToNow(new Date(q.created_at), { addSuffix: true })}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  <Button data-testid={`quote-pdf-${q.id}`} size="sm" variant="outline"
                          onClick={() => downloadPdf(q.id, q.number)} className="h-8">
                    <FileText size={14} className="mr-1"/>PDF
                  </Button>
                  {q.status !== "converted" && (
                    <Button data-testid={`quote-convert-${q.id}`} size="sm"
                            onClick={() => convert(q.id)}
                            className="h-8" style={{ background: "var(--miro-green)" }}>
                      <FileCheck2 size={14} className="mr-1"/>Convert
                    </Button>
                  )}
                  <button data-testid={`quote-delete-${q.id}`} onClick={() => remove(q.id)}
                          className="text-[var(--miro-text-muted)] hover:text-red-600 p-1.5 inline-flex">
                    <Trash2 size={14}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}

function NewQuotationDialog({ open, setOpen, onCreated }) {
  const newLine = () => ({ _key: crypto.randomUUID(), description: "", quantity: 1, unit_price: 0 });
  const [f, setF] = useState({
    title: "", client_name: "", client_email: "", client_phone: "",
    gst_percent: 18, discount: 0, validity_days: 30, notes: "",
    line_items: [newLine()],
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  const updateItem = (i, patch) => {
    const next = [...f.line_items];
    next[i] = { ...next[i], ...patch };
    upd("line_items", next);
  };
  const addItem = () => upd("line_items", [...f.line_items, newLine()]);
  const removeItem = (i) => upd("line_items", f.line_items.filter((_, idx) => idx !== i));

  const subtotal = f.line_items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
  const afterDisc = Math.max(subtotal - (Number(f.discount) || 0), 0);
  const gst = afterDisc * (Number(f.gst_percent) || 0) / 100;
  const total = afterDisc + gst;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/quotations", {
        ...f,
        client_email: f.client_email || null,
        client_phone: f.client_phone || null,
        gst_percent: Number(f.gst_percent) || 0,
        discount: Number(f.discount) || 0,
        validity_days: Number(f.validity_days) || 30,
        line_items: f.line_items
          .filter(li => li.description.trim())
          .map(li => ({ ...li, quantity: Number(li.quantity) || 0, unit_price: Number(li.unit_price) || 0 })),
      });
      toast.success("Quotation created");
      setOpen(false);
      setF({ title: "", client_name: "", client_email: "", client_phone: "",
             gst_percent: 18, discount: 0, validity_days: 30, notes: "",
             line_items: [newLine()]});
      onCreated?.();
    } catch (e) { toast.error("Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-quote-btn" style={{ background: "var(--miro-navy)" }}><Plus size={16} className="mr-1"/> New quotation</Button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New quotation</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Title *</Label><Input data-testid="new-quote-title" value={f.title} onChange={e=>upd("title",e.target.value)} className="mt-1"/></div>
            <div><Label>Client name *</Label><Input data-testid="new-quote-client-name" value={f.client_name} onChange={e=>upd("client_name",e.target.value)} className="mt-1"/></div>
            <div><Label>Client email</Label><Input data-testid="new-quote-client-email" type="email" value={f.client_email} onChange={e=>upd("client_email",e.target.value)} className="mt-1"/></div>
            <div><Label>Client phone</Label><Input data-testid="new-quote-client-phone" value={f.client_phone} onChange={e=>upd("client_phone",e.target.value)} className="mt-1"/></div>
            <div><Label>Validity (days)</Label><Input data-testid="new-quote-validity" type="number" value={f.validity_days} onChange={e=>upd("validity_days",e.target.value)} className="mt-1"/></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Line items</Label>
              <Button data-testid="new-quote-add-line" size="sm" variant="outline" onClick={addItem} className="h-8"><Plus size={13} className="mr-1"/>Add row</Button>
            </div>
            <div className="rounded-lg border border-[var(--miro-border)] divide-y divide-[var(--miro-border)]">
              {f.line_items.map((li, i) => (
                <div key={li._key} data-testid={`new-quote-line-${i}`} className="grid grid-cols-12 gap-2 p-2 items-center">
                  <Input data-testid={`new-quote-line-desc-${i}`} placeholder="Description" value={li.description} onChange={e=>updateItem(i,{description:e.target.value})} className="col-span-6 h-9"/>
                  <Input data-testid={`new-quote-line-qty-${i}`} type="number" placeholder="Qty" value={li.quantity} onChange={e=>updateItem(i,{quantity:e.target.value})} className="col-span-2 h-9"/>
                  <Input data-testid={`new-quote-line-price-${i}`} type="number" placeholder="Unit ₹" value={li.unit_price} onChange={e=>updateItem(i,{unit_price:e.target.value})} className="col-span-3 h-9"/>
                  <button data-testid={`new-quote-line-remove-${i}`} onClick={() => removeItem(i)} disabled={f.line_items.length === 1} className="col-span-1 text-[var(--miro-text-muted)] hover:text-red-600 disabled:opacity-30 flex justify-center">
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 items-end">
            <div>
              <Label>GST %</Label>
              <Select value={String(f.gst_percent)} onValueChange={v=>upd("gst_percent",Number(v))}>
                <SelectTrigger data-testid="new-quote-gst" className="mt-1"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {[0, 5, 12, 18, 28].map(g => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Discount (₹)</Label><Input data-testid="new-quote-discount" type="number" value={f.discount} onChange={e=>upd("discount",e.target.value)} className="mt-1"/></div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-[var(--miro-text-muted)]">Total</div>
              <div className="font-display font-black text-2xl text-[var(--miro-navy)]" data-testid="new-quote-total">₹{Math.round(total).toLocaleString("en-IN")}</div>
              <div className="text-[11px] text-[var(--miro-text-muted)]">
                Subtotal ₹{Math.round(subtotal).toLocaleString("en-IN")} · GST ₹{Math.round(gst).toLocaleString("en-IN")}
              </div>
            </div>
          </div>

          <div>
            <Label>Notes (client-facing)</Label>
            <Textarea data-testid="new-quote-notes" rows={2} value={f.notes} onChange={e=>upd("notes",e.target.value)} className="mt-1"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button data-testid="new-quote-submit"
                  disabled={!f.title.trim()||!f.client_name.trim()||saving||subtotal<=0}
                  onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Creating…" : <><Sparkles size={14} className="mr-1"/>Create quotation</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
